import React from "react";
import {
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  MenuItem,
  Snackbar,
  Alert
} from "@mui/material";
import useAPI from "../../../hooks/useAPI";
import axios from "axios";
import { BURL } from "../../../services/base";
import { render } from "@testing-library/react";

const EditProfile = () => {
  const [profileImage, setProfileImage] = React.useState("https://via.placeholder.com/100");
  const [base64Image, setBase64Image] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [successOpen, setSuccessOpen] = React.useState(false);
  const [errorOpen, setErrorOpen] = React.useState(false);
  const [errorText, setErrorText] = React.useState("");

  const { GET, PUT } = useAPI();

  const [initialProfile, setInitialProfile] = React.useState(null);

  const [profile, setProfile] = React.useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    stateOfOrigin: "",
    address: "",
    dob: "",
    gender: "",
    occupation: "",
    workplace: "",
    beenInPoliceCustody: false,
    convictedOfFelony: false,
    emergencyContactName: "",
    emergencyContactPhone: "",
    emergencyContactRelationship: "",
    dp: ""
  });

  // Load admin details
  const getAdminDetails = React.useCallback(async () => {
    setLoading(true);
    // const response = await GET("admin");
    const params = {
      full: true
    };

    const response = await axios.get(`${BURL}admin`, {
      params,
      // headers: { Authorization: `Bearer ${token}` }
    });
    setLoading(false);

    if (response?.status) {
      const user = response.data.data.user;
      console.log('Hello:', response.data.data.user);

      // Convert Yes/No to booleans
      const fixedUser = {
        ...user,
        // beenInPoliceCustody: user.beenInPoliceCustody === "Yes",
        // convictedOfFelony: user.convictedOfFelony === "Yes"
      };

      profile.firstname = user.firstname || "";
      profile.lastname = user.lastname || "";
      profile.email = user.email || "";
      profile.phone = user.phone || "";
      profile.stateOfOrigin = user.stateOfOrigin || "";
      profile.address = user.address || "";
      // profile.dob = user.dob || "";
      profile.dob = user.dob
        ? user.dob.split("T")[0]
        : "";
      profile.gender = user.gender || "";
      profile.occupation = user.occupation || "";
      profile.workplace = user.workplace || "";
      profile.beenInPoliceCustody = user.beenInPoliceCustody || false;
      profile.convictedOfFelony = user.convictedOfFelony || false;
      profile.emergencyContactName = user.emergencyContactName || "";
      profile.emergencyContactPhone = user.emergencyContactPhone || "";
      profile.emergencyContactRelationship = user.emergencyContactRelationship || "";
      profile.dp = user.dp || ""

      // setProfile(fixedUser);
      setInitialProfile(fixedUser);
      setProfileImage(user.dp || profileImage);
    } else {
      setErrorText(response.message ?? "Failed to load profile.");
      setErrorOpen(true);
    }
  }, []);

  React.useEffect(() => {
    getAdminDetails();
  }, []);

  // Update fields
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resizeImage = (file, maxWidth = 600, quality = 0.7) =>
    new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => (img.src = e.target.result);

      img.onload = () => {
        const scale = maxWidth / img.width;
        const canvas = document.createElement("canvas");

        canvas.width = maxWidth;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        resolve(canvas.toDataURL("image/jpeg", quality));
      };

      reader.readAsDataURL(file);
    });


  // Checkbox updates
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: checked
    }));
  };

  // Image upload
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const compressedBase64 = await resizeImage(file);
    // const base64Only = compressedBase64.split(",")[1];
    setBase64Image(compressedBase64);
    setProfileImage(compressedBase64);
    profile.dp = compressedBase64;
    // const file = event.target.files[0];
    // if (file) {
    //   const reader = new FileReader();
    //   reader.onload= () => {
      //     setProfileImage(reader.result);
    //   };
    //   reader.readAsDataURL(file);
    // }
  };

  // Detect changes
  const isChanged = JSON.stringify(profile) !== JSON.stringify(initialProfile);

  // Submit update
  const handleFinalSubmit = async () => {
    setLoading(true);

    try {
      const payload = {
        ...profile,
        phone: profile.phone || "",
        // Send dp (image)
        // dp: base64Image || profile.dp
      };

      const response = await PUT("admin", payload);

      if (response?.status) {
        setSuccessOpen(true);
        setInitialProfile(payload);
      } else {
        setErrorText(response.message || "Failed to update profile");
        setErrorOpen(true);
      }
    } catch (err) {
      setErrorText("Error updating profile.");
      setErrorOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="max-w-4xl min-h-screen mx-auto p-6 rounded-lg">

        {/* Profile Image */}
        <div className="flex items-center space-x-4">
          <img
            src={profileImage}
            className="w-32 h-32 rounded-full border-2 border-gray-300"
            alt=""
          />

          <label htmlFor="file-upload" className="cursor-pointer">
            <div className="flex items-center justify-center w-10 h-10 bg-green-600 text-white rounded-full">
              📸
            </div>
          </label>

          <input
            id="file-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {/* Basic Info */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField
            label="First name"
            name="firstname"
            value={profile.firstname}
            onChange={handleProfileChange}
            sx={{ "& .MuiInputBase-input": { textTransform: "capitalize" } }}
            fullWidth
          />

          <TextField
            label="Last name"
            name="lastname"
            value={profile.lastname}
            onChange={handleProfileChange}
            sx={{ "& .MuiInputBase-input": { textTransform: "capitalize" } }}
            fullWidth
          />

          <TextField
            label="Phone number"
            name="phone"
            value={profile.phone}
            onChange={handleProfileChange}
            fullWidth
          />

          <TextField
            label="Email"
            name="email"
            value={profile.email}
            onChange={handleProfileChange}
            fullWidth
          />
        </div>

        {/* Gender */}
        <div className="mt-4">
          <FormControl>
            <FormLabel>Gender</FormLabel>
            <RadioGroup
              row
              name="gender"
              value={profile.gender}
              onChange={handleProfileChange}
            >
              <FormControlLabel checked={profile.gender === "male"} value="Male" control={<Radio />} label="Male" />
              <FormControlLabel checked={profile.gender === "female"} value="Female" control={<Radio />} label="Female" />
            </RadioGroup>
          </FormControl>
        </div>

        {/* Additional Info */}
        <div className="mt-6 bg-white p-4 rounded-lg shadow-sm">
          <h2 className="text-lg font-semibold text-green-600">Additional Information</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <TextField
              type="date"
              label="Date Of Birth"
              name="dob"
              value={profile.dob}
              onChange={handleProfileChange}
              fullWidth
            />

            <TextField
              label="State Of Origin"
              name="stateOfOrigin"
              value={profile.stateOfOrigin}
              onChange={handleProfileChange}
              sx={{ "& .MuiInputBase-input": { textTransform: "capitalize" } }}
              fullWidth
            />

            <TextField
              label="Address"
              name="address"
              value={profile.address}
              onChange={handleProfileChange}
              fullWidth
            />

            <TextField
              label="What do you do?"
              name="occupation"
              select
              value={profile.occupation}
              onChange={handleProfileChange}
              fullWidth
            >
              <MenuItem value="Student">Student</MenuItem>
              <MenuItem value="Employed">Employed</MenuItem>
            </TextField>

            <TextField
              label="Workplace/School"
              name="workplace"
              value={profile.workplace}
              onChange={handleProfileChange}
              fullWidth
            />
          </div>

          <div className="flex flex-col sm:flex-row mt-4 space-y-2 sm:space-y-0 sm:space-x-4">

            <FormControlLabel
              control={
                <Checkbox
                  name="beenInPoliceCustody"
                  checked={profile.beenInPoliceCustody}
                  onChange={handleCheckboxChange}
                />
              }
              label="Have you ever been in police custody?"
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="convictedOfFelony"
                  checked={profile.convictedOfFelony}
                  onChange={handleCheckboxChange}
                />
              }
              label="Have you ever been convicted of a felony?"
            />
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="mt-6 bg-white p-4 rounded-lg shadow-sm">
          <h2 className="text-lg font-semibold text-green-600">Emergency Contact Details / Next of Kin</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <TextField
              label="Full Name"
              name="emergencyContactName"
              value={profile.emergencyContactName}
              onChange={handleProfileChange}
              fullWidth
            />

            <TextField
              label="Phone Number"
              name="emergencyContactPhone"
              value={profile.emergencyContactPhone}
              onChange={handleProfileChange}
              fullWidth
            />

            <TextField
              label="Relationship"
              name="emergencyContactRelationship"
              value={profile.emergencyContactRelationship}
              onChange={handleProfileChange}
              fullWidth
            />
          </div>
        </div>

        {/* Submit */}
        <button
          disabled={!isChanged || loading}
          onClick={handleFinalSubmit}
          className={`mt-6 px-8 py-4 rounded-md text-white 
            ${!isChanged || loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}
          `}
        >
          {loading ? "Saving..." : "Submit Profile"}
        </button>
      </div>

      {/* Success Toast */}
      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        onClose={() => setSuccessOpen(false)}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccessOpen(false)}>
          Profile updated successfully!
        </Alert>
      </Snackbar>

      {/* Error Toast */}
      <Snackbar
        open={errorOpen}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        onClose={() => setErrorOpen(false)}
      >
        <Alert severity="error" variant="filled" onClose={() => setErrorOpen(false)}>
          {errorText}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default EditProfile;
